import React from 'react';
import { StyleSheet, View, ViewStyle } from 'react-native';

import {
	IndicatorAddSvg,
	IndicatorCheckSvg,
	IndicatorLockSvg,
	IndicatorRemoveSvg,
} from '../../../assets/achievements';
import { achievementsColors } from './achievementsTheme';

/**
 * Figma "Indicator / Achievement" (4082:26519) - the corner status badge on an achievement card,
 * and the node markers on the detail modal's tier rail. The file's note:
 *
 *   State=Selected - success green fill with a white check (shown on the profile).
 *   State=Locked - surface/raised fill with a gold padlock.
 *   State=Empty - surface/raised fill with a text/muted outline, no glyph.
 *   State=Remove - surface/overlay fill with a text/secondary outline and a white X.
 *   18x18, fully round.
 *
 * The master carries a fifth state the note predates - State=Add (4476:25983), a surface/raised
 * plate with a gold/200 rim and plus - and that is what an unlocked card that is not on the
 * profile wears. It ships as one SVG with its own plate, so it is drawn whole rather than as a
 * glyph inside the plate below.
 */
export type IndicatorState = 'selected' | 'locked' | 'empty' | 'remove' | 'add';

export const INDICATOR_SIZE = 18;

const GLYPH: Partial<Record<IndicatorState, { Icon: React.ComponentType<any>; size: number }>> = {
	selected: { Icon: IndicatorCheckSvg, size: 13 },
	locked: { Icon: IndicatorLockSvg, size: 13 },
	remove: { Icon: IndicatorRemoveSvg, size: 12 },
};

const PLATE: Partial<Record<IndicatorState, { fill: string; border: string }>> = {
	selected: { fill: achievementsColors.green, border: achievementsColors.surface },
	locked: { fill: achievementsColors.surface, border: achievementsColors.lockedIndicatorBorder },
	empty: { fill: achievementsColors.surface, border: achievementsColors.muted },
	remove: { fill: achievementsColors.overlay, border: achievementsColors.secondary },
};

type Props = {
	state: IndicatorState;
	scale: number;
	/** The rail draws the same badge two points larger. */
	size?: number;
	style?: ViewStyle | ViewStyle[];
};

export const AchievementIndicator = ({ state, scale, size = INDICATOR_SIZE, style }: Props) => {
	const plate = PLATE[state];
	const glyph = GLYPH[state];

	// State=Add ships as one SVG, plate and all, so it is drawn whole.
	if (state === 'add') {
		return (
			<View style={[styles.indicator, { width: size * scale, height: size * scale }, style]}>
				<IndicatorAddSvg width={size * scale} height={size * scale} />
			</View>
		);
	}

	return (
		<View
			style={[
				styles.indicator,
				{
					width: size * scale,
					height: size * scale,
					borderRadius: (size / 2) * scale,
					borderWidth: Math.max(1, 1 * scale),
					backgroundColor: plate?.fill,
					borderColor: plate?.border,
				},
				style,
			]}
		>
			{/* Wrapped for the same painting-order reason as the inventory tabs' icons: a bare SVGR
			    <svg> has no position of its own on web and slides under positioned siblings. */}
			{glyph ? (
				<View>
					<glyph.Icon width={glyph.size * scale} height={glyph.size * scale} />
				</View>
			) : null}
		</View>
	);
};

const styles = StyleSheet.create({
	indicator: {
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
	},
});

export default AchievementIndicator;
